import { createSelector } from "@reduxjs/toolkit"
import { RootState } from "./store"
import { GLOBAL_TENANT_HINT, SUPER_ADMIN_TENANT_ID } from "./constants/tenant"

// ── Base selectors — simple property access ──────────────────────────

export const selectApplications = (state: RootState) => state.applications
export const selectActiveApp = (state: RootState) => state.activeApp
export const selectUserToken = (state: RootState) => state.userToken
export const selectUserInfo = (state: RootState) => state.userInfo
export const selectActiveMenu = (state: RootState) => state.activeMenu
export const selectPageTitle = (state: RootState) => state.pageTitle
export const selectAppTitle = (state: RootState) => state.appTitle
export const selectShowGlobalLoader = (state: RootState) =>
  state.showGlobalLoader
export const selectIsSettingsPage = (state: RootState) => state.isSettingsPage
export const selectSettingsMenu = (state: RootState) => state.settingsMenu
export const selectApplicationMessages = (state: RootState) =>
  state.applicationMessages
export const selectEulaConfig = (state: RootState) => state.eulaConfig
export const selectEnvironment = (state: RootState) => state.environment
export const selectFoneAlertConfig = (state: RootState) =>
  state.foneAlertConfig
export const selectAIPanel = (state: RootState) => state.aiPanel
export const selectShowHomepage = (state: RootState) => state.showHomepage
export const selectLocalAuthRequired = (state: RootState) =>
  state.localAuthRequired
export const selectUseNewIframe = (state: RootState) => state.useNewIframe
export const selectMountedApps = (state: RootState) => state.mountedApps
export const selectRoutePrefix = (state: RootState) => state.routePrefix
export const selectAppPrefix = (state: RootState) => state.appPrefix

// ── Derived / memoized selectors ─────────────────────────────────────
// Use createSelector when the selector computes derived data or returns
// a new object/array reference. This prevents unnecessary re-renders
// in useCoreSelector / subscriptionManager.

/** Active app's API URL (most common use case in consumer modules) */
export const selectActiveAppApiUrl = createSelector(
  selectActiveApp,
  (app) => app?.modules?.moduleUrls?.apiURL ?? null
)

/** Active app's URL prefix */
export const selectActiveAppPrefix = createSelector(
  selectActiveApp,
  (app) => app?.modules?.moduleUrls?.prefix ?? null
)

/** Active app's application code */
export const selectActiveAppCode = createSelector(
  selectActiveApp,
  (app) => app?.applicationCode ?? null
)

/** Whether the AI panel is open */
export const selectIsAIPanelOpen = createSelector(
  selectAIPanel,
  (panel) => panel?.isOpen ?? false
)

/** Whether AI is enabled for this tenant */
export const selectIsAIEnabled = createSelector(
  selectAIPanel,
  (panel) => panel?.isEnabled ?? false
)

/** AI panel alert count */
export const selectAIPanelAlertCount = createSelector(
  selectAIPanel,
  (panel) => panel?.alerts?.length ?? 0
)

/** Context published by the active page to drive a custom ARIA surface. */
export const selectAISurfaceContext = createSelector(
  selectAIPanel,
  (panel) => panel?.surfaceContext ?? null
)

/** Whether the global alert modal is visible */
export const selectIsAlertVisible = createSelector(
  selectFoneAlertConfig,
  (config) => config.status
)

/** Whether the user is authenticated (has a valid token) */
export const selectIsAuthenticated = createSelector(
  selectUserToken,
  (token) => token !== null
)

/** User's display name from userInfo */
export const selectUserName = createSelector(
  selectUserInfo,
  (info) => info?.userName ?? null
)

/** Global tenant ID from user info */
export const selectGlobalTenantId = createSelector(
  selectUserInfo,
  (info) =>
    info?.ext?.tenantId?.find((t) => t?.hint === GLOBAL_TENANT_HINT)?.id ??
    null
)

/** Whether the current user is a super admin */
export const selectIsSuperAdmin = createSelector(
  selectGlobalTenantId,
  (id) => id === SUPER_ADMIN_TENANT_ID
)

/** List of SPA applications only */
export const selectSPAApplications = createSelector(
  selectApplications,
  (apps) => apps.filter((app) => app.isSPA)
)

/** List of iframe-based applications only */
export const selectIframeApplications = createSelector(
  selectApplications,
  (apps) => apps.filter((app) => app.openInIframe)
)
